import { Typography } from "@circle-react-uikit/Typography";
import type { Options } from "@popperjs/core";
import classNames from "classnames";
import { noop } from "lodash";
import type { UseControllerProps } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";

import type {
  TextSelectOption,
  TextSelectProps,
} from "@/react/components/SettingsApp/CompassCompat/TextSelect";
import { TextSelect } from "@/react/components/SettingsApp/CompassCompat/TextSelect";

export interface FormTextSelectProps<
  Option extends TextSelectOption = TextSelectOption,
> extends TextSelectProps<Option> {
  appendTo?: HTMLElement | null;
  hideLabel?: boolean;
  hideSelectedItems?: boolean;
  inputContainerClassName?: string;
  labelClassName?: string;
  minSearchValueLength?: number;
  name: string;
  popperOptions?: Partial<Options>;
  rules?: UseControllerProps["rules"];
  searchable?: boolean;
  showSearchIcon?: boolean;
  tooltipText?: string;
  variant?: string;
  wrapperClassName?: string;
}

export const FormTextSelect = <
  Option extends TextSelectOption = TextSelectOption,
>({
  name,
  onChange = noop,
  onClear,
  label,
  rules = {},
  wrapperClassName,
  initialValue,
  ...rest
}: FormTextSelectProps<Option>) => {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMessage = fieldState.error?.message;
        const hasError = Boolean(errorMessage);
        const value = field.value as Option["value"];

        return (
          <div
            className={classNames(
              "flex flex-col gap-1.5",
              {
                "text-v2-danger": hasError,
              },
              wrapperClassName,
            )}
          >
            <TextSelect<Option>
              value={value}
              label={label}
              isError={hasError}
              initialValue={initialValue}
              onChange={value => {
                field.onChange(value);
                onChange?.(value);

                setValue(name, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }}
              onClear={() => {
                setValue(name, null, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
                onClear?.();
              }}
              {...rest}
            />
            {hasError && (
              <Typography.LabelXs>
                <span
                  className={classNames({
                    "text-v2-danger": hasError,
                  })}
                >
                  {errorMessage}
                </span>
              </Typography.LabelXs>
            )}
          </div>
        );
      }}
    />
  );
};

FormTextSelect.displayName = "Form.TextSelect";
